import type { Store } from '@reduxjs/toolkit';

import { config } from '../../config';
import messagesEN from '../../features/translations/en-GB.json';
import { reportErrorToSentry } from '../setup/sentry';
import { trace } from '../setup/trace';
import { DEFAULT_LOCALE, getSupportedLocale as defaultGetSupportedLocale } from './lang';
import { type DisplayMessages, displayMessagesFetched, localeChanged } from './langSlice';

const displayMessageLoaders = import.meta.glob<DisplayMessages>(
    ['../../features/translations/*.json', '!../../features/translations/en-GB.json'],
    { import: 'default' }
);

const sendError = (exception: unknown) => {
    reportErrorToSentry(exception, {
        tags: {
            module: config.sentryModuleName,
        },
    });
};

export const importDisplayMessages = (locale: string): Promise<DisplayMessages> =>
    (locale === DEFAULT_LOCALE
        ? Promise.resolve(messagesEN)
        : (displayMessageLoaders[`../../features/translations/${locale}.json`]?.() ??
          Promise.reject(new Error(`No display messages found for locale "${locale}"`)))
    ).catch(error => {
        sendError(error);
        throw error;
    });

export const configureFetchDisplayMessages =
    (store: Store, fetchDisplayMessages = importDisplayMessages, getSupportedLocale = defaultGetSupportedLocale) =>
    async (locale: string) => {
        if (!locale) {
            console.warn('No "locale" supplied when fetching display messages!');
            return Promise.reject();
        }

        const supportedLocale = getSupportedLocale(locale);

        try {
            const displayMessages = await fetchDisplayMessages(supportedLocale);
            trace(`Display messages fetched for "${supportedLocale}"`);
            store.dispatch(displayMessagesFetched({ locale: supportedLocale, displayMessages }));
        } catch (error: unknown) {
            console.error(`Display messages for "${supportedLocale}" could not be fetched.`, error);
            sendError(error);
            store.dispatch(localeChanged(DEFAULT_LOCALE));
        }
    };
